<template>
  <div class="dcb-code-teaser">
    <div class="dcb-code-teaser__title">
      <a
        id="zahlung-per-handyrechnung-nutzen"
        style="position: absolute; margin-top: -80px"
      />
      <Heading
        tag="h2"
        size="md"
        spaces-bottom
      >
        Zahlung per Handyrechnung nutzen
      </Heading>
      <Paragraph>
        Zahlungsoption auf dem Smartphone einrichten oder einfach online nutzen um in Zukunft alle
        Einkäufe über deine Telekom Handyrechnung oder dein Prepaid-Guthaben zu zahlen.
      </Paragraph>
    </div>
    <div class="dcb-code-teaser__image">
      <ImageElement
        :alt="teaserImage.alt"
        render-without-figure
        lazy
        :src="teaserImage.src"
      />
    </div>
    <div class="dcb-code-teaser__copy">
      <TabGroup
        :tabs="tabs"
        class="dcb-code-teaser__tabgroup"
      >
        <template #ios>
          <TabGroupEntry>
            <Paragraph>
              Mit der Einrichtung der Zahlmethode auf dem Smartphone kannst du zum Beispiel
              App-Käufe komfortabel per Handyrechnung oder Prepaid-Guthaben zahlen.
            </Paragraph>

            <StepsGeneric @teaser-step="setTeaserImage" />

            <AccordionItem class="dcb-code-teaser__manual">
              <template #title>Manuelle Einrichtung</template>
              <template #content>
                <StepsAppleManual />
              </template>
            </AccordionItem>
          </TabGroupEntry>
        </template>
        <template #android>
          <TabGroupEntry>
            <Paragraph>
              Mit der Einrichtung der Zahlmethode auf dem Smartphone kannst du zum Beispiel
              App-Käufe komfortabel per Handyrechnung oder Prepaid-Guthaben zahlen.
            </Paragraph>

            <StepsGeneric
              is-google
              @teaser-step="setTeaserImage"
            />

            <AccordionItem class="dcb-code-teaser__manual">
              <template #title>Manuelle Einrichtung</template>
              <template #content>
                <StepsGoogleManual />
              </template>
            </AccordionItem>
          </TabGroupEntry>
        </template>
      </TabGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  AccordionItem,
  Heading,
  ImageElement,
  Paragraph,
} from '@i22-td-smarthome/component-library';
import TabGroup from '@/components/tab-group.vue';
import TabGroupEntry from '@/components/tab-group-entry.vue';
import StepsGeneric from '@/components/dcb/steps-generic.vue';
import StepsGoogleManual from '@/components/dcb/steps-google-manual.vue';
import StepsAppleManual from '@/components/dcb/steps-apple-manual.vue';

const tabs = [
  { name: 'ios', label: 'iOS/Apple' },
  { name: 'android', label: 'Android/Google' },
];

interface Image {
  src: string;
  alt: string;
}

const defaultTeaserImage = {
  src: '/content/content-images/dcb/a9a388d6b9c19e60a7c26ac275c445ef/dcb-teaser-qr.svg',
  alt: 'Eine Frau steht vor einem großen QR-Code und hält ein Handy in der Hand.',
};
const teaserImage = ref<Image>(defaultTeaserImage);

const setTeaserImage = (image: Image) => {
  teaserImage.value = image || defaultTeaserImage;
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-code-teaser {
  padding-block: $grid-base * 2;
  margin-bottom: $grid-base * 5;

  @include mq($mq-sm) {
    display: grid;
    grid-template-areas: 'title title' 'copy image';
    grid-template-columns: 1fr 1fr;
  }

  &__title {
    grid-area: title;
    align-self: end;
  }
  &__copy {
    grid-area: copy;
  }
  &__image {
    grid-area: image;
    margin-bottom: $grid-base * 2;

    img {
      display: block;
      width: 100%;
      max-height: 560px;
      aspect-ratio: 463 / 301;
    }

    @include mq($mq-sm) {
      margin-top: $grid-base * 2;
      align-self: center;
    }
  }

  &__tabgroup {
    padding-inline: 0;
  }

  &__manual {
    margin-top: $grid-base * 2;
    --theme-primary-color: transparent;
    --bg-color-hover: transparent;
    --bg-color-active: transparent;

    :deep(.accordion-item__title) {
      color: var(--color-link);
    }
  }
}
</style>
